const elementHandlers = new Map<Element, Set<() => void>>();
let observer: ResizeObserver | undefined;

/**
 * Calls `handler` when `element` resizes, through one ResizeObserver shared by every caller:
 * a long transcript with a text block per message does not mean one observer per block.
 * Спільний ResizeObserver на всіх: довгий чат не створює окремий спостерігач на кожен блок.
 */
export function observeResize(element: Element, handler: () => void): () => void {
  if (typeof ResizeObserver === "undefined") return () => {};
  observer ??= new ResizeObserver((entries) => {
    for (const entry of entries) {
      for (const notify of [...(elementHandlers.get(entry.target) ?? [])]) notify();
    }
  });
  let handlers = elementHandlers.get(element);
  if (!handlers) {
    handlers = new Set();
    elementHandlers.set(element, handlers);
    observer.observe(element);
  }
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
    if (handlers.size > 0 || elementHandlers.get(element) !== handlers) return;
    elementHandlers.delete(element);
    observer?.unobserve(element);
    if (elementHandlers.size === 0) {
      observer?.disconnect();
      observer = undefined;
    }
  };
}

const viewportHandlers = new Set<() => void>();
const notifyViewportHandlers = () => {
  for (const notify of [...viewportHandlers]) notify();
};

/** Calls `handler` when the window or the visual viewport resizes, with one listener of each. */
export function onViewportResize(handler: () => void): () => void {
  if (viewportHandlers.size === 0) {
    window.addEventListener("resize", notifyViewportHandlers);
    window.visualViewport?.addEventListener("resize", notifyViewportHandlers);
  }
  viewportHandlers.add(handler);
  return () => {
    viewportHandlers.delete(handler);
    if (viewportHandlers.size > 0) return;
    window.removeEventListener("resize", notifyViewportHandlers);
    window.visualViewport?.removeEventListener("resize", notifyViewportHandlers);
  };
}
